<script setup lang="ts">
import {
  watch, onMounted, computed,
} from 'vue';

import { useI18n } from 'vue-i18n';
import { isEqual } from 'lodash-es';

import { LpButtonGroup } from '@thehotelsnetwork/lampo';

import SectionActions from '@/modules/DataSections/components/SectionActions/SectionActions.vue';
import SectionFilters from '@/modules/DataSections/components/SectionFilters/SectionFilters.vue';
import SectionMetricChange from '@/modules/DataSections/components/SectionMetricChange/SectionMetricChange.vue';
import SectionViewSwitch from '@/modules/DataSections/components/SectionViewSwitch/SectionViewSwitch.vue';
// State
import useMainState from '@/state/useMainState';
import useUserState from '@/state/useUserState';
import useSectionState from '@/state/useSectionState';
import useFilterState from '@/state/useFilterState';

// Data
import getStats from '@/utils/getStats';

// Components
import SectionDateSelectors from '@/modules/DataSections/components/SectionDateSelectors/SectionDateSelectors.vue';

import TableSection from '@/components/Table/TableSection.vue';
import Score from '@/components/Score/Score.vue';
import Table from '@/components/Table/Table.vue';

import CalendarSection from '@/modules/DataSections/components/Calendar/CalendarSection.vue';
import ChartSection from '@/modules/DataSections/components/Chart/ChartSection.vue';
import type { Table as TableType } from '@/declarations/main';

import vScrollReveal from '@/directives/scroll-reveal';

// Directives

const props = defineProps<{
  title: string,
  hideFilters?: boolean,
  hideViews?: boolean,
}>();

const { t } = useI18n();
const { isOverview } = useMainState();
const { isStrictlyDMOUser } = useUserState();

const {
  dateBegin,
  dateEnd,
  ctpDateBegin,
  ctpDateEnd,
  sdDateBegin,
  sdDateEnd,
  devices,
  geolocations,
  filteredOtas,
  travelParty,
  hotelType,
  hotelCategory,
  source,
  applyCtpStayDates,
  ctpTag,
  selectedChartType,
  selectedChartSubType,
} = useFilterState();

const {
  isChartsView,
  isTablesView,
  isCalendarView,
  overviewTableSection,
  sectionName,
  breakdownPropertyCallList,
  sectionHasScore,
  shouldGetOverviewStats,
  setShouldGetOverviewStats,
  sectionView,
  setSectionTitle,
  sectionTables,
  sectionCharts,
  sectionMetrics,
  isBreakdownOverviewSection,
} = useSectionState();

setSectionTitle(props.title);

const summaryTable = computed(() => sectionTables.value
  .filter((table: any) => table.type === 'summary')[0]);

const shouldShowSummaryTable = computed(() => isTablesView.value
  && !isBreakdownOverviewSection.value
  && sectionMetrics.value.length > 1
  && summaryTable.value);

const overviewSwitchOptions = computed(() => [
  { value: 'overview', label: t('Overview') },
  { value: 'breakdown', label: t('Hotels Breakdown') },
]);

const breakdownEnabled = computed(() => sectionTables.value
  .filter((t: any) => t.type === 'breakdown' && t.valid).length);

const showBreakdownSwitch = computed(
  () => !!(isTablesView.value
    && isOverview.value
    && breakdownEnabled.value
    && !isStrictlyDMOUser.value),
);

onMounted(() => {
  watch(
    [
      sdDateBegin,
      sdDateEnd,
      devices,
      geolocations,
      travelParty,
      hotelType,
      hotelCategory,
      source,
    ],
    () => getStats({ reset: true }),
    { flush: 'post' },
  );

  watch(
    filteredOtas,
    (next, prev) => {
      if (!isEqual(prev, next)) {
        getStats({ reset: true });
      }
    },
    { flush: 'post' },
  );

  watch(
    [dateBegin, dateEnd],
    () => getStats({
      reset: true,
      getGeolocationStats: true,
    }),
    { flush: 'post' },
  );

  watch([
    ctpDateBegin,
    ctpDateEnd,
  ], (_, [ctpDateBeginOld, ctpDateEndOld]) => {
    const shouldResetData = isBreakdownOverviewSection.value || (ctpDateBeginOld !== 0
      && ctpDateEndOld !== 0);
    getStats({
      reset: shouldResetData,
      isCtpCall: !shouldResetData,
      forced: !shouldResetData,
    });
  });

  watch(applyCtpStayDates, () => {
    if (ctpTag.value) {
      getStats({ reset: true });
    }
  });

  watch([selectedChartType, selectedChartSubType], getStats, { flush: 'post' });

  watch([overviewTableSection, breakdownPropertyCallList], ([sect, list]) => {
    if ((sect === 'breakdown' && !list.length) // breakdown table view but no properties pending to load?
      || (sect === 'overview' && !shouldGetOverviewStats.value)) { // overview table/charts view but shouldn't fetch data?
      // don't make any calls
      return;
    }

    /*
      Proceeds ONLY if:
      - is breakdown table view & there are properties pending to load.
      - is overview/chart view & data was reset during breakdown view (filters change)
    */

    if (sect === 'overview' && shouldGetOverviewStats.value) {
      // set flag back to false after getting OV stats
      setShouldGetOverviewStats(false);
    }

    // get stats without resetting data/compsets
    getStats({ reset: false });
  });

  const isPrevOrNext = (prev: string, next: string, name: string) => prev === name || next === name;

  watch(
    sectionView,
    (next, prev) => {
      if (isPrevOrNext(prev, next, 'calendar')) {
        getStats({ reset: true });
      } else if (isPrevOrNext(prev, next, 'tables') && overviewTableSection.value === 'breakdown') {
        getStats({ reset: false });
      }
    },
    { flush: 'post' },
  );
});
</script>

<template>
  <div>
    <div
      v-if="!sectionTables.length && !sectionCharts.length"
      class="absolute top-0 left-0 w-full h-full z-modal bg-base-100/80"
    >
      <Loader
        size="big"
      />
    </div>
    <div class="w-full">
      <!-- Main Section Header -->
      <div>
        <!-- First Section -->
        <div class="relative flex flex-wrap justify-between z-100">
          <div class="flex justify-start mt-4">
            <div>
              <p class="section-title">
                {{ t(title) }}
              </p>
            </div>
            <Score
              v-if="!isOverview && sectionHasScore"
            />
          </div>
          <div class="z-20 flex items-center gap-4 mt-4 filters">
            <SectionDateSelectors />

            <template
              v-if="!hideViews"
            >
              <div>
                <span class="filter-separator" />
              </div>
              <SectionViewSwitch />
            </template>
          </div>
        </div>
        <!-- Filters -->
        <div class="flex flex-col w-full gap-8 mt-8 mb-8 sectionContent__first">
          <SectionFilters />

          <div
            v-if="showBreakdownSwitch"
            class="flex flex-row-reverse w-full breakdown-switch"
          >
            <LpButtonGroup
              :key="sectionView"
              v-model="overviewTableSection"
              :options="overviewSwitchOptions"
            />
          </div>

          <Table
            v-if="shouldShowSummaryTable"
            v-scroll-reveal
            :table="summaryTable as unknown as TableType"
          />

          <div>
            <SectionMetricChange />
            <SectionActions>
              <template #customActions>
                <slot name="customActions" />
              </template>
            </SectionActions>
          </div>
        </div>
      </div>
    </div>

    <div>
      <!-- Charts -->
      <ChartSection
        v-if="isChartsView"
      />

      <!-- Calendar -->
      <CalendarSection
        v-if="isCalendarView"
      />

      <!-- Tables -->
      <TableSection
        v-if="isTablesView"
      />
    </div>
  </div>
</template>

<style scoped lang="postcss">
.section-user-input > div:not(:last-of-type) {
  @apply mt-8;
}
</style>
